import { match } from 'ts-pattern';
import { getIdSequence, memoEval } from '@salsita-ai/platform-project-sdk/domain';
import { $BottomCabinetWithAppliance, $BottomWallAppliance, $CountertopAppliance, $GrillBase, $TopAppliance } from '../__generated/enhanced-model.js';
import { attachComponent, implementCustomExpressions, implementCustomInvariants } from '../__generated/implementation.js';
import { PulsePowderCoatableRoomItem, RearPanel, SwatchPowderCoatableRoomItem, WallDirection } from '../__generated/model/index.js';
import { cornerFiller, partData, rearPanelSizes } from '../data/parts.js';
import { Model, PricingType, getCurrentUserPrice } from '../index.js';
import { stackComponent } from '../other/stack-component.js';
import { StackInvariants } from '../other/stack-invariants.js';
import { isDealer } from '../util/auth.js';
import { notEmpty } from '../util/not-empty.js';
import { round } from '../util/round.js';
import { CabinetWidth } from './common.js';
const ActiveWallsByShape = {
    I: {
        A: false,
        B: true,
        C: false
    },
    L: {
        A: true,
        B: true,
        C: false
    },
    U: {
        A: true,
        B: true,
        C: true
    }
};
export const getBottomWallStackId = (wallId)=>`Bottom${wallId}`;
export const getTopWallStackId = (wallId)=>`Top${wallId}`;
export const getCountertopWallStackId = (wallId)=>`Countertop${wallId}`;
export const getWallIdFromStackKey = (stackKey)=>{
    return stackKey.replace(/^Bottom|Top|Countertop/, '');
};
export const customWallComponents = [
    attachComponent.withEntity('Wall').withComponent(stackComponent, 'wallBottom').attach(memoEval((s)=>[
            s.id,
            s.$stackMin,
            s.$stackMax
        ], (wallId, min, max)=>{
        return {
            parentEntityId: wallId,
            stackKey: getBottomWallStackId(wallId),
            min: round(min, 2),
            max: round(max, 2)
        };
    })),
    attachComponent.withEntity('Wall').withComponent(stackComponent, 'wallTop').attach(memoEval((s)=>[
            s.id,
            s.$stackMin,
            s.$stackMax
        ], (wallId, min, max)=>{
        return {
            parentEntityId: wallId,
            stackKey: getTopWallStackId(wallId),
            min: round(min, 2),
            max: round(max, 2)
        };
    })),
    attachComponent.withEntity('Wall').withComponent(stackComponent, 'wallCountertop').attach(memoEval((s)=>[
            s.id,
            s.$stackMin,
            s.$stackMax
        ], (wallId, min, max)=>{
        return {
            parentEntityId: wallId,
            stackKey: getCountertopWallStackId(wallId),
            min: round(min, 2),
            max: round(max, 2)
        };
    }))
];
export const wallExpressions = implementCustomExpressions({
    Wall: {
        $name: (s)=>{
            return {
                id: "wallLengthInput.label",
                message: "Run {0}",
                values: {
                    0: s.wallType
                }
            };
        },
        $active: (s)=>{
            return ActiveWallsByShape[s.$room.shape][s.wallType];
        },
        $direction: (s)=>{
            return match(s.wallType).with('A', ()=>WallDirection.left).with('B', ()=>WallDirection.right).with('C', ()=>WallDirection.right).exhaustive();
        },
        $stackMin: (s)=>{
            return match(s.$room.shape).with('I', ()=>match(s.wallType).with('A', ()=>0).with('B', ()=>-Infinity).with('C', ()=>0).exhaustive()).with('L', ()=>match(s.wallType).with('A', ()=>-Infinity).with('B', ()=>0).with('C', ()=>0).exhaustive()).with('U', ()=>match(s.wallType).with('A', ()=>-Infinity).with('B', ()=>0).with('C', ()=>0).exhaustive()).exhaustive();
        },
        $stackMax: (s)=>{
            return match(s.$room.shape).with('I', ()=>match(s.wallType).with('A', ()=>0).with('B', ()=>Infinity).with('C', ()=>0).exhaustive()).with('L', ()=>match(s.wallType).with('A', ()=>s.length).with('B', ()=>Infinity).with('C', ()=>0).exhaustive()).with('U', ()=>match(s.wallType).with('A', ()=>s.length)// One side gap - just for the first corner cabinet.
                .with('B', ()=>s.length - 2 * s.$room.$cornerCabinetWidth - s.$room.$cabinetSideGap).with('C', ()=>Infinity).exhaustive()).exhaustive();
        },
        $stackFillFrom: (s)=>{
            return match(s.$room.shape).with('I', ()=>match(s.wallType).with('A', ()=>0).with('B', ()=>0).with('C', ()=>0).exhaustive()).with('L', ()=>match(s.wallType).with('A', ()=>s.length).with('B', ()=>0).with('C', ()=>0).exhaustive()).with('U', ()=>match(s.wallType).with('A', ()=>s.length).with('B', ()=>0).with('C', ()=>0).exhaustive()).exhaustive();
        },
        $stackFillTo: (s)=>{
            return match(s.$room.shape).with('I', ()=>match(s.wallType).with('A', ()=>0).with('B', ()=>s.length).with('C', ()=>0).exhaustive()).with('L', ()=>match(s.wallType).with('A', ()=>s.$room.$cornerCabinetWidth + s.$room.$cabinetSideGap).with('B', ()=>s.length - s.$room.$cornerCabinetWidth - s.$room.$cabinetSideGap).with('C', ()=>0).exhaustive()).with('U', ()=>match(s.wallType).with('A', ()=>s.$room.$cornerCabinetWidth + s.$room.$cabinetSideGap).with('B', ()=>s.length - 2 * s.$room.$cornerCabinetWidth - s.$room.$cabinetSideGap).with('C', ()=>s.length - s.$room.$cornerCabinetWidth - s.$room.$cabinetSideGap).exhaustive()).exhaustive();
        },
        $startOffset: (s)=>{
            return match(s.$room.shape).with('I', ()=>0).with('L', ()=>match(s.wallType).with('A', ()=>0).with('B', ()=>s.$room.$cornerCabinetWidth + s.$room.$cabinetSideGap).with('C', ()=>0).exhaustive()).with('U', ()=>match(s.wallType).with('A', ()=>0).with('B', ()=>s.$room.$cornerCabinetWidth + s.$room.$cabinetSideGap).with('C', ()=>s.$room.$cornerCabinetWidth + s.$room.$cabinetSideGap).exhaustive()).exhaustive();
        },
        $endOffset: (s)=>{
            return match(s.$room.shape).with('I', ()=>0).with('L', ()=>match(s.wallType).with('A', ()=>s.$room.$cornerCabinetWidth + s.$room.$cabinetSideGap).with('B', ()=>0).with('C', ()=>0).exhaustive()).with('U', ()=>match(s.wallType).with('A', ()=>s.$room.$cornerCabinetWidth + s.$room.$cabinetSideGap)// No gap at the end of the wall.
                .with('B', ()=>s.$room.$cornerCabinetWidth).with('C', ()=>0).exhaustive()).exhaustive();
        },
        $x: (s)=>{
            return match(s.wallType).with('A', ()=>-s.$room.$width / 2).with('B', ()=>-s.$room.$width / 2 + s.$room.$cornerCabinetWidth + s.$room.$cabinetSideGap).with('C', ()=>s.$room.$width / 2).exhaustive();
        },
        $wallObjectX: (s)=>{
            return match(s.wallType).with('A', ()=>s.$x).with('B', ()=>s.$room.shape === 'I' ? s.$x : s.$x - s.$room.$cornerCabinetWidth - s.$room.$cabinetSideGap).with('C', ()=>s.$x).exhaustive();
        },
        $y: (s)=>{
            return s.hidden ? -10000 : 0;
        },
        $z: (s)=>{
            return match(s.wallType).with('A', ()=>-s.$room.$depth / 2 + s.length + s.$room.$wallB.$maxWallItemDepth + s.$room.$cornerFillerExtraSpace).with('B', ()=>-s.$room.$depth / 2).with('C', ()=>-s.$room.$depth / 2 + s.$room.$wallB.$maxWallItemDepth + s.$room.$cornerFillerExtraSpace).exhaustive();
        },
        $wallObjectZ: (s)=>{
            return match(s.wallType).with('A', ()=>s.$z - s.$room.$wallB.$maxWallItemDepth - s.$room.$cornerFillerExtraSpace).with('B', ()=>s.$z).with('C', ()=>s.$z - s.$room.$wallB.$maxWallItemDepth - s.$room.$cornerFillerExtraSpace).exhaustive();
        },
        $rotationY: (s)=>{
            return match(s.wallType).with('A', ()=>Math.PI / 2).with('B', ()=>0).with('C', ()=>-Math.PI / 2).exhaustive();
        },
        $height: (s)=>{
            return s.fullHeight ? s.$fullHeight : s.$hiddenHeight;
        },
        $minHeight: (s)=>{
            return s.fullHeight ? Math.max(s.$maxWallItemHeight, s.$room.$minCabinetHeight) + s.$countertopThickness + 1 * s.$room.$inchToMm : s.$hiddenHeight;
        },
        $minLength: (s)=>{
            return match([
                s.$room.shape,
                s.wallType
            ]).with([
                'U',
                'B'
            ], ()=>80 * s.$room.$inchToMm).otherwise(()=>36 * s.$room.$inchToMm);
        },
        $draggingSurface: (s)=>{
            return match(s.wallType).with('A', ()=>({
                    key: s.id,
                    normal: {
                        x: 1,
                        y: 0,
                        z: 0
                    },
                    constant: -s.$x
                })).with('B', ()=>({
                    key: s.id,
                    normal: {
                        x: 0,
                        y: 0,
                        z: 1
                    },
                    constant: -s.$z
                })).with('C', ()=>({
                    key: s.id,
                    normal: {
                        x: -1,
                        y: 0,
                        z: 0
                    },
                    constant: s.$x
                })).exhaustive();
        },
        $maxWallItemDepth: (s)=>{
            return Math.max(s.$bottomItems.reduce((acc, c)=>Math.max(acc, c.$depth), 0), s.$room.$minCabinetDepth);
        },
        $maxWallItemHeight: (s)=>{
            return s.$bottomItems.reduce((acc, c)=>Math.max(acc, c.$height), 0);
        },
        $rearPanelWidths: (s)=>{
            const rearPanelWidths = [];
            const sortedItems = [
                ...s.$bottomItems
            ].sort((a, b)=>a.x - b.x);
            // Calculate the rear panel widths by finding continuous/uninterrupted cabinet spaces.
            if (s.$active && !s.fullHeight && sortedItems.length > 0) {
                let previousItem;
                let longestContinuousCabinetSpace = s.$startOffset;
                let continuousSpaceStartX = s.$stackFillFrom - s.$startOffset;
                for(let i = 0; i < sortedItems.length; i++){
                    const currentItem = sortedItems[i];
                    const currentOffset = previousItem ? previousItem.x + previousItem.$width : 0;
                    if (currentOffset !== currentItem.x) {
                        const endX = continuousSpaceStartX + longestContinuousCabinetSpace;
                        rearPanelWidths.push({
                            width: longestContinuousCabinetSpace,
                            minX: continuousSpaceStartX,
                            maxX: endX
                        });
                        longestContinuousCabinetSpace = 0;
                        continuousSpaceStartX = currentItem.x;
                    }
                    longestContinuousCabinetSpace += currentItem.$width;
                    previousItem = currentItem;
                }
                if (previousItem && Math.abs(s.$stackFillTo - (previousItem.x + previousItem.$width)) > s.$room.$ignoredCabinetSpace) {
                    const endX = continuousSpaceStartX + longestContinuousCabinetSpace;
                    rearPanelWidths.push({
                        width: longestContinuousCabinetSpace,
                        minX: continuousSpaceStartX,
                        maxX: endX
                    });
                    longestContinuousCabinetSpace = s.$endOffset;
                    continuousSpaceStartX = s.$stackFillTo;
                } else {
                    longestContinuousCabinetSpace += s.$endOffset;
                }
                if (longestContinuousCabinetSpace > 0) {
                    const endX = continuousSpaceStartX + longestContinuousCabinetSpace;
                    rearPanelWidths.push({
                        width: longestContinuousCabinetSpace,
                        minX: continuousSpaceStartX,
                        maxX: endX
                    });
                }
            }
            return rearPanelWidths;
        },
        $continuousCountertopSpaces: (s)=>{
            const countertopSpaces = [];
            // Sort items by X position (smallest to largest)
            const sortedItems = [
                ...s.$bottomItems
            ].sort((a, b)=>a.x - b.x);
            // Calculate continuous countertop spaces, interrupted by items without work surfaces or gaps
            if (s.$active && sortedItems.length > 0) {
                let continuousSpaceStartX = null;
                let currentX = null;
                for(let i = 0; i < sortedItems.length; i++){
                    const currentItem = sortedItems[i];
                    const itemStart = currentItem.x;
                    const itemEnd = currentItem.x + currentItem.$width;
                    // Skip items without work surface - they interrupt continuous spaces
                    if (!currentItem.$hasWorkSurface) {
                        // Close current continuous space if one exists
                        if (continuousSpaceStartX !== null && currentX !== null) {
                            countertopSpaces.push({
                                width: currentX - continuousSpaceStartX,
                                minX: continuousSpaceStartX,
                                maxX: currentX
                            });
                        }
                        // Reset tracking
                        continuousSpaceStartX = null;
                        currentX = null;
                        continue;
                    }
                    // Item has work surface
                    if (continuousSpaceStartX === null || currentX === null) {
                        // Start a new continuous space
                        continuousSpaceStartX = itemStart;
                        currentX = itemEnd;
                    } else if (currentX === itemStart) {
                        // Item is continuous with previous - extend the space
                        currentX = itemEnd;
                    } else {
                        // There's a gap - close current space and start new one
                        countertopSpaces.push({
                            width: currentX - continuousSpaceStartX,
                            minX: continuousSpaceStartX,
                            maxX: currentX
                        });
                        continuousSpaceStartX = itemStart;
                        currentX = itemEnd;
                    }
                }
                // Add the final continuous space if one exists
                if (continuousSpaceStartX !== null && currentX !== null) {
                    countertopSpaces.push({
                        width: currentX - continuousSpaceStartX,
                        minX: continuousSpaceStartX,
                        maxX: currentX
                    });
                }
            }
            return countertopSpaces;
        },
        $summary: (s, state, ctx)=>{
            // Calculate the rear panel parts.
            const rearPanelParts = s.$rearPanelWidths.flatMap((panelSpace)=>{
                const panelWidth = panelSpace.width;
                if (panelWidth <= CabinetWidth.W96) {
                    const panelSize = rearPanelSizes.find((size)=>size >= panelWidth);
                    if (!panelSize) {
                        return [];
                    }
                    return [
                        panelSize
                    ];
                } else {
                    const panelCount = Math.ceil(panelWidth / CabinetWidth.W96);
                    const singlePanelWidth = panelWidth / panelCount;
                    const panelSize = rearPanelSizes.find((size)=>size >= singlePanelWidth);
                    if (!panelSize) {
                        return [];
                    }
                    return Array.from({
                        length: panelCount
                    }, (_, i)=>{
                        if (i === panelCount - 1) {
                            return panelWidth - (panelCount - 1) * panelSize;
                        }
                        return panelSize;
                    });
                }
            }).flatMap((panelWidth, panelIndex)=>{
                const nearestPanelWidth = rearPanelSizes.find((size)=>size >= round(panelWidth, 2));
                const panelData = partData[`${RearPanel.type}_${nearestPanelWidth}`];
                if (!panelData) {
                    return [];
                }
                const rearPanelId = `RearPanel-${panelIndex}`;
                const dealerPrice = getCurrentUserPrice(ctx.publishedData.prices?.[panelData.sku], {
                    userType: PricingType.Dealer,
                    powderCoatType: s.$room.powderCoatType,
                    includeSwatchUpcharge: SwatchPowderCoatableRoomItem.isId(rearPanelId),
                    includePulseUpcharge: PulsePowderCoatableRoomItem.isId(rearPanelId)
                });
                const retailPrice = getCurrentUserPrice(ctx.publishedData.prices?.[panelData.sku], {
                    userType: PricingType.Retail,
                    powderCoatType: s.$room.powderCoatType,
                    includeSwatchUpcharge: SwatchPowderCoatableRoomItem.isId(rearPanelId),
                    includePulseUpcharge: PulsePowderCoatableRoomItem.isId(rearPanelId)
                });
                const rearPanelPublishedData = ctx.publishedData.prices?.[panelData.sku];
                if (!rearPanelPublishedData) {
                    throw new Error(`No published product data found for sku: ${panelData.sku}`);
                }
                return [
                    {
                        id: `RearPanel-${panelIndex}`,
                        name: panelData.name,
                        sku: panelData.sku,
                        dealerPrice,
                        retailPrice,
                        currentUserPrice: isDealer(ctx.sessionData) ? dealerPrice : retailPrice,
                        packageDepth: rearPanelPublishedData.packageDepth,
                        packageHeight: rearPanelPublishedData.packageHeight,
                        packageWidth: rearPanelPublishedData.packageWidth,
                        packageWeight: rearPanelPublishedData.packageWeight
                    }
                ];
            });
            const sequence = getIdSequence(state.entities, []);
            const cornerFillerId = sequence.nextId('CornerFiller');
            const cornerFillerDealerPrice = getCurrentUserPrice(ctx.publishedData.prices?.[cornerFiller.sku], {
                userType: PricingType.Dealer,
                powderCoatType: s.$room.powderCoatType,
                includeSwatchUpcharge: SwatchPowderCoatableRoomItem.isId(cornerFillerId),
                includePulseUpcharge: PulsePowderCoatableRoomItem.isId(cornerFillerId)
            });
            const cornerFillerRetailPrice = getCurrentUserPrice(ctx.publishedData.prices?.[cornerFiller.sku], {
                userType: PricingType.Retail,
                powderCoatType: s.$room.powderCoatType,
                includeSwatchUpcharge: SwatchPowderCoatableRoomItem.isId(cornerFillerId),
                includePulseUpcharge: PulsePowderCoatableRoomItem.isId(cornerFillerId)
            });
            const cornerFillerPublishedData = ctx.publishedData.prices?.[cornerFiller.sku];
            if (!cornerFillerPublishedData) {
                throw new Error(`No published product data found for sku: ${cornerFiller.sku}`);
            }
            const cornerFillerWithPrice = {
                id: cornerFillerId,
                name: cornerFiller.name,
                sku: cornerFiller.sku,
                dealerPrice: cornerFillerDealerPrice,
                retailPrice: cornerFillerRetailPrice,
                currentUserPrice: isDealer(ctx.sessionData) ? cornerFillerDealerPrice : cornerFillerRetailPrice,
                packageDepth: cornerFillerPublishedData.packageDepth,
                packageHeight: cornerFillerPublishedData.packageHeight,
                packageWidth: cornerFillerPublishedData.packageWidth,
                packageWeight: cornerFillerPublishedData.packageWeight
            };
            const cornerFillerParts = [
                ...s.wallType === Model.WallType.A && (s.$room.shape === Model.RoomShapeType.L || s.$room.shape === Model.RoomShapeType.U) ? [
                    cornerFillerWithPrice
                ] : [],
                ...s.wallType === Model.WallType.C && s.$room.shape === Model.RoomShapeType.U ? [
                    cornerFillerWithPrice
                ] : []
            ];
            const cabinets = [
                ...s.$cabinets
            ].sort((c1, c2)=>c1.x - c2.x).flatMap((c)=>c.$summary.map((summary)=>({
                        ...summary,
                        thumbnailUrl: c.$thumbnailUrl
                    })));
            const parts = [
                ...[
                    ...s.$parts
                ].flatMap((p)=>p.$summary),
                ...cornerFillerParts,
                ...rearPanelParts
            ];
            const appliances = [
                ...s.$cabinets,
                ...s.$wallAppliances,
                ...s.$topItems,
                ...s.$countertopItems
            ].sort((c1, c2)=>c1.x - c2.x).flatMap((c)=>match(c).when($BottomWallAppliance.is, (wallAppliance)=>[
                        wallAppliance
                    ]).when($TopAppliance.is, (topAppliance)=>[
                        topAppliance
                    ]).when($GrillBase.is, (grillBase)=>[
                        grillBase.$appliance,
                        grillBase.$warmingDrawer
                    ]).when($BottomCabinetWithAppliance.is, (cabinetWithAppliance)=>[
                        cabinetWithAppliance.$appliance
                    ]).when($CountertopAppliance.is, (countertopAppliance)=>[
                        countertopAppliance
                    ]).otherwise(()=>[])).filter(notEmpty).flatMap((a)=>a.$summary);
            const applianceDealerPrice = appliances.reduce((acc, item)=>acc + item.dealerPrice, 0);
            const applianceRetailPrice = appliances.reduce((acc, item)=>acc + item.retailPrice, 0);
            const cabinetDealerPrice = cabinets.reduce((acc, item)=>acc + item.dealerPrice, 0);
            const cabinetRetailPrice = cabinets.reduce((acc, item)=>acc + item.retailPrice, 0);
            const partDealerPrice = parts.reduce((acc, item)=>acc + item.dealerPrice, 0);
            const partRetailPrice = parts.reduce((acc, item)=>acc + item.retailPrice, 0);
            const dealerPrices = {
                appliances: applianceDealerPrice,
                cabinetsAndParts: cabinetDealerPrice + partDealerPrice
            };
            const retailPrices = {
                appliances: applianceRetailPrice,
                cabinetsAndParts: cabinetRetailPrice + partRetailPrice
            };
            const currentUserPrices = isDealer(ctx.sessionData) ? dealerPrices : retailPrices;
            return {
                id: s.id,
                name: s.$name,
                length: s.length,
                cabinets,
                parts,
                appliances,
                dealerPrices,
                retailPrices,
                currentUserPrices
            };
        },
        $dimensionIndicatorLabelFontSize: (s)=>{
            // Use a minimum of 8 and maximum of 16, scaling linearly with wall length (in mm)
            // Example: 10pt font at 3000mm, 12pt at 6000mm, clamped between 8-16
            const minFont = 60;
            const maxFont = 150;
            const minLength = 3000 // wall length below this gives minFont
            ;
            const maxLength = 15000 // wall length above this gives maxFont
            ;
            const length = s.length;
            if (length <= minLength) {
                return minFont;
            }
            if (length >= maxLength) {
                return maxFont;
            }
            const fontSize = minFont + (length - minLength) / (maxLength - minLength) * (maxFont - minFont);
            return Math.round(fontSize);
        },
        $dimensionIndicatorCabinetsStartX: (s)=>{
            return match(s.wallType).with('A', ()=>s.length + s.$room.$wallB.$maxWallItemDepth + s.$room.$cornerFillerExtraSpace).with('B', ()=>s.$dimensionIndicatorWallStartX).with('C', ()=>s.$dimensionIndicatorWallStartX).exhaustive();
        },
        $dimensionIndicatorCabinetsEndX: (s)=>{
            if (s.wallType === 'A') {
                return s.$bottomItems.reduce((acc, item)=>Math.min(acc, item.x), s.length);
            } else if (s.wallType === 'B') {
                return s.$bottomItems.reduce((acc, item)=>{
                    return Math.max(acc, item.x + item.$width);
                }, 0);
            } else {
                return s.$bottomItems.reduce((acc, item)=>{
                    return Math.max(acc, item.x + item.$width);
                }, 0);
            }
        },
        $dimensionIndicatorCabinetsY: (s)=>s.$maxWallItemHeight,
        $dimensionIndicatorRemainingStartX: (s)=>s.$dimensionIndicatorCabinetsEndX,
        $dimensionIndicatorRemainingEndX: (s)=>s.$stackFillTo,
        $dimensionIndicatorRemainingExtrusionZ: (s)=>s.$maxWallItemDepth + 200,
        $dimensionIndicatorRemainingFilter: (s)=>{
            if (!s.$active) {
                return false;
            }
            const remainingSpace = Math.abs(s.$dimensionIndicatorRemainingStartX - s.$dimensionIndicatorRemainingEndX);
            if (remainingSpace < s.$room.$ignoredCabinetSpace) {
                return false;
            }
            return true;
        },
        $dimensionIndicatorWallStartX: (s)=>{
            return match(s.wallType).with('A', ()=>s.$wallObjectX - s.$x + s.$room.$cornerFillerExtraSpace + s.$room.$wallB.$maxWallItemDepth).with('B', ()=>s.$wallObjectX - s.$x).with('C', ()=>s.$wallObjectX - s.$x - s.$room.$cornerFillerExtraSpace - s.$room.$wallB.$maxWallItemDepth).exhaustive();
        },
        $dimensionIndicatorWallEndX: (s)=>{
            return s.length + s.$dimensionIndicatorWallStartX;
        },
        $dimensionIndicatorWallLabel: (s)=>{
            return `${s.wallType}: ${round(s.length / s.$room.$inchToMm, 2)}"`;
        },
        $dimensionIndicatorCabinetHeightFilter: (s)=>s.$bottomItems.length > 0,
        $dimensionIndicatorCabinetHeightX: (s)=>s.$dimensionIndicatorCabinetsStartX,
        $dimensionIndicatorCabinetHeightExtrusionX: (s)=>{
            return match(s.wallType).with('A', ()=>250).with('B', ()=>-200).with('C', ()=>-250).exhaustive();
        },
        $dimensionIndicatorCabinetHeightY: (s)=>s.$maxWallItemHeight
    }
});
export const wallInvariants = implementCustomInvariants({
    Wall: {
        ...StackInvariants
    }
});

//# sourceMappingURL=wall.js.map